<template>
    <card title="Price Schedule">
        <b-row id="search-row" align-v="end" v-show="!newMode">
            <b-col>
                <SearchComponent v-model="priceSchedule" label="Price Schedule" search-u-r-l="/PriceSchedule"
                                 :params="{ includeInactive: true }"
                                 prepopulate clearable :disabled="!disableFields" @input="fetchPriceSchedule"/>
            </b-col>
            <b-col cols="2">
                <b-button block variant="danger" @click="clear" :disabled="disableFields">Clear</b-button>
            </b-col>
        </b-row>

        <b-form v-if="priceSchedule">

            <b-form-row id="base-row">
                <b-col>
                    <b-form-group label="Name*" :disabled="disableFields">
                        <b-form-input v-model="priceSchedule.name"/>
                        <b-form-invalid-feedback :state="isFormValid">
                            Name is required
                        </b-form-invalid-feedback>
                    </b-form-group>
                </b-col>
                <b-col cols="4">
                    <enum-selector label="Price Basis*" v-model="priceSchedule.basis" :enum-codes="BasisEnum"
                                   :disabled="disableFields"/>
                    <b-form-invalid-feedback :state="isFormValid">
                        Price Basis is required
                    </b-form-invalid-feedback>
                </b-col>
            </b-form-row>

            <b-form-row id="config-row">
                <b-col>
                    <b-form-group label="Price Factor" :disabled="disableFields">
                        <b-form-input v-model.number="priceSchedule.factor" type="number"/>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group label="Markup" :disabled="disableFields">
                        <b-form-input v-model.number="priceSchedule.markup" type="number"/>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group label="Service Fee" :disabled="disableFields">
                        <b-form-input v-model.number="priceSchedule.serviceFee" type="number"/>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group label="Margin Override Amount" :disabled="disableFields">
                        <b-form-input v-model.number="priceSchedule.marginOverrideAmt" type="number"/>
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group label="Minimum Total" :disabled="disableFields">
                        <b-form-input v-model.number="priceSchedule.minimum" type="number"/>
                    </b-form-group>
                </b-col>
            </b-form-row>
            <b-form-row>
                <b-col cols="5">
                    <label>Programs</label>
                    <HelpPopup>
                        Programs that this price schedule is available for.
                        If none are selected, the price schedule will be available to all programs.
                    </HelpPopup>
                    <multiselect v-model="selectedPrograms"
                                 group-label="label"
                                 group-values="programs"
                                 track-by="id"
                                 :options="programOptions"
                                 :close-on-select="false"
                                 multiple
                                 group-select
                                 :disabled="disableFields"
                    />
                </b-col>
                <b-col cols="5">
                    <label>Stores</label>
                    <HelpPopup>
                        Stores that this price schedule is available for.
                        If none are selected, the price schedule will be available to all stores.
                    </HelpPopup>
                    <multiselect v-model="selectedStores"
                                 group-label="label"
                                 group-values="stores"
                                 track-by="id"
                                 :options="storeOptions"
                                 :close-on-select="false"
                                 multiple
                                 group-select
                                 :disabled="disableFields"
                    />
                </b-col>
            </b-form-row>

            <b-row>

                <b-col>
                    <b-form-row id="checkbox-row">
                        <b-col cols="auto">
                            <b-form-group label="Flat Markup" :disabled="disableFields">
                                <b-form-checkbox size="sm" v-model="priceSchedule.flatMarkup"/>
                            </b-form-group>
                        </b-col>
                        <b-col cols="auto">
                            <b-form-group label="Flat Service Fee" :disabled="disableFields">
                                <b-form-checkbox size="sm" v-model="priceSchedule.flatServiceFee"/>
                            </b-form-group>
                        </b-col>
                        <b-col cols="auto">
                            <b-form-group label="Factor Out Sales Tax" :disabled="disableFields">
                                <b-form-checkbox size="sm" v-model="priceSchedule.factorOutSalesTax"/>
                            </b-form-group>
                        </b-col>
                        <b-col cols="auto">
                            <b-form-group :disabled="disableFields">
                                <label>
                                    Include Dispensing Fee in Base
                                    <HelpPopup>
                                        When checked, the NonGsdd dispensing fee is included in the base price before
                                        price factor and markup are applied (legacy behavior — enabled by default for
                                        existing schedules). When unchecked, the dispensing fee is added as a flat
                                        amount after all factor, markup, and service fee calculations.
                                    </HelpPopup>
                                </label>
                                <b-form-checkbox size="sm" v-model="priceSchedule.includeDispensingFeeInBase"/>
                            </b-form-group>
                        </b-col>

                        <b-col cols="12"/>

                        <b-col cols="auto">
                            <b-form-group label="Active" :disabled="disableFields">
                                <b-form-checkbox size="sm" v-model="priceSchedule.isActive"/>
                            </b-form-group>
                        </b-col>
                        <b-col cols="auto">
                            <b-form-group label="Consignment" :disabled="disableFields">
                                <b-form-checkbox size="sm" v-model="priceSchedule.isConsignment"/>
                            </b-form-group>
                        </b-col>
                        <b-col cols="auto">
                            <b-form-group label="Offline" :disabled="disableFields">
                                <b-form-checkbox size="sm" v-model="priceSchedule.isOffline"/>
                            </b-form-group>
                        </b-col>
                        <b-col>
                            <b-form-group :disabled="disableFields">
                                <label>
                                    Use Linear Interpolation
                                    <HelpPopup>
                                        <p>
                                            When enabled, unit prices for drugs will be linearly
                                            interpolated between pricing entries.
                                        </p>
                                        <br>
                                        <p><strong>Example:</strong></p>
                                        <ul>
                                            <li>Entry 1: Quantity 30 at $1.00</li>
                                            <li>Entry 2: Quantity 90 at $0.50</li>
                                            <li>
                                                Price for Quantity 60:
                                                <ul>
                                                    <li>Slope: (0.50 - 1.00) / (90 - 30) = -0.0083</li>
                                                    <li>Intercept: 1.00 - (-0.0083 * 30) = 1.249</li>
                                                    <li>Final Unit Price: (-0.0083 * 60) + 1.249 = $0.75</li>
                                                </ul>
                                            </li>
                                        </ul>
                                    </HelpPopup>
                                </label>
                                <b-form-checkbox size="sm" v-model="priceSchedule.useLinearInterpolation"/>
                            </b-form-group>
                        </b-col>
                    </b-form-row>
                </b-col>
                <b-col class="text-right">
                    <b-button variant="outline-primary" size="sm" @click="showPriceTestModal = true"
                              :disabled="disableFields">
                        Drug Price Test...
                    </b-button>
                </b-col>
            </b-row>

            <b-form-row id="quantity-pricing-row">
                <b-col>
                    <quantity-pricing-component ref="qty-pricing" :price-schedule="priceSchedule"
                                                :disabled="disableFields" :on-add="savePriceSchedule"/>
                </b-col>
            </b-form-row>

            <!-- Member Tier Management -->
            <b-form-row v-if="priceSchedule.id && showStripeFeatures" id="member-tier-row">
                <b-col>
                    <member-tier-management
                        :price-schedule-id="priceSchedule.id"
                        @member-tier-saved="onMemberTierChanged"
                        @member-tier-deleted="onMemberTierChanged"
                        @member-tier-reactivated="onMemberTierChanged"
                    />
                </b-col>
            </b-form-row>
        </b-form>

        <NewClearSaveButtons ref="newClearSaveButtons" :disable="disableFields" :disable-clear="disableFields" :disable-save="!isFormValid"
                             :titles="{ new: 'New', save: 'Save', clear: 'Clear' }" disable-save-after-click
                             @new-clicked="newClicked"
                             @clear-clicked="clear" @cancel-clicked="clear"
                             @save-clicked="saveClicked" />

        <PriceTestModal v-if="showPriceTestModal" @hide="showPriceTestModal = false" :price-schedule="priceSchedule"
                        :on-add="savePriceSchedule"/>
    </card>

</template>

<script lang="ts">
import { Component, Ref, Vue, Watch } from 'vue-property-decorator';
import SearchComponent from "@/components/SearchComponent.vue";
import { BasisEnum, PriceSchedule } from "@/models/PriceSchedule";
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import EnumSelector from "@/components/EnumSelector.vue";
import QuantityPricingComponent from "@/components/Drug/QuantityPricingComponent.vue";
import NewClearSaveButtons from "@/components/NewClearSaveButtons.vue";
import PriceTestModal from '@/components/Drug/PriceTestModalComponent.vue';
import HelpPopup from "@/components/HelpPopup.vue";
import Multiselect from "vue-multiselect";
import MemberTierManagement from "@/components/PriceSchedule/MemberTierManagement.vue";
import { Program } from "@/models/Program";
import { Store } from "@/models/Store";
import StripeProductService from "@/services/StripeProductService";

@Component({
    components: {
        NewClearSaveButtons,
        QuantityPricingComponent,
        EnumSelector,
        SearchComponent,
        PriceTestModal,
        HelpPopup,
        Multiselect,
        MemberTierManagement,
    },
})
export default class PriceSchedulePage extends Vue {
    @Ref('qty-pricing') quantityPricingComponent!: QuantityPricingComponent;
    @Ref() newClearSaveButtons!: NewClearSaveButtons;
    priceSchedule: PriceSchedule = new PriceSchedule();
    newMode: boolean = false;
    showPriceTestModal = false;
    showStripeFeatures: boolean = false;

    programs: Program[] = [];
    selectedPrograms: Program[] = [];
    programOptions = [{ label: "All Programs", programs: [] as Program[] }];

    stores: Store[] = [];
    selectedStores: Store[] = [];
    storeOptions = [{ label: "All Stores", stores: [] as Store[] }];

    get BasisEnum() {
        return BasisEnum;
    }

    get routeId(): number {
        let res = parseInt(this.$route.params.id);
        if (isNaN(res)) return 0;
        return res;
    }

    get isFormValid(): boolean {
        return this.priceSchedule?.name?.length > 0 && this.priceSchedule?.basis !== undefined;
    }

    get disableFields(): boolean {
        const lockedForUser = false; // not sure if we want to implement locks on the price schedule or not...
        const noEntityLoaded = !(this.priceSchedule && this.priceSchedule.id);

        return (noEntityLoaded || lockedForUser) && !this.newMode;
    }

    async created() {
        this.fetchPriceSchedule({ id: this.routeId } as PriceSchedule);
        // Check if Stripe products are available
        this.showStripeFeatures = await StripeProductService.hasStripeProducts();
    }

    mounted() {
        this.fetchProgramsAndStores();
    }

    @Watch('stores')
    @Watch('programs')
    @Watch('priceSchedule')
    updateSelectedPrograms(){
        this.selectedPrograms = this.programs.filter(p => this.priceSchedule.programIds?.includes(p.id));
        this.selectedStores = this.stores.filter(s=>this.priceSchedule.storeIds?.includes(Number(s.id)));
    }

    async fetchProgramsAndStores() {
        try {
            const programs = await axios.get<Program[]>("/api/Program");
            this.programs = programs.data.map<Program>(d => new Program(d));
            this.programOptions = [{ label: "All Programs", programs: this.programs }];
            const stores = await axios.get<Store[]>("/api/Store");
            this.stores = stores.data.map<Store>(d => new Store(d));
            this.storeOptions = [{ label: "All Stores", stores: this.stores }];
        } catch (e) {
            this.$notification(NotificationOptions.error(e));
        }
    }

    clear() {
        this.newMode = false;
        if (this.routeId) this.fetchPriceSchedule();
        if (this.quantityPricingComponent?.clearDrug) this.quantityPricingComponent.clearDrug();
        this.priceSchedule = new PriceSchedule();
    }

    newClicked() {
        this.clear();
        this.newMode = true;
    }

    async saveClicked() {
        console.log("save clicked");
        let saved = await this.savePriceSchedule();
        if (saved) {
            this.newClearSaveButtons.resetSaveState();
            await this.fetchPriceSchedule(saved);
        }
    }

    async savePriceSchedule() {
        try {
            if (this.priceSchedule.marginOverrideAmt == undefined
                || this.priceSchedule.marginOverrideAmt as unknown == "") {
                this.priceSchedule.marginOverrideAmt = null;
            }

            if (this.priceSchedule.minimum == undefined || this.priceSchedule.minimum as unknown == "") {
                this.priceSchedule.minimum = null;
            }

            this.priceSchedule.programIds = this.selectedPrograms.map(p=>p.id);
            this.priceSchedule.storeIds = this.selectedStores.map(s=>Number(s.id));
            const resp = await axios.post<PriceSchedule>("/api/PriceSchedule/", this.priceSchedule);
            this.priceSchedule = new PriceSchedule(resp.data);
            this.$notification(NotificationOptions.successSaveNotificationPreset("Price Schedule"));
            return this.priceSchedule;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async fetchPriceSchedule(entity?: PriceSchedule) {
        if (!entity || !entity.id) {
            if (this.routeId) {
                await this.$router.push({ name: "PriceSchedule" });
            }
            this.clear();
            return;
        }

        if (entity.id && entity.id != this.routeId) {
            await this.$router.push({ name: "PriceScheduleDetail", params: { id: entity.id.toString() } });
            return;
        }

        try {
            const resp = await axios.get<PriceSchedule>(`/api/PriceSchedule/${entity.id}`);
            this.priceSchedule = new PriceSchedule(resp.data);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }

    }

    onMemberTierChanged() {
        // Refresh the price schedule data when member tiers change
        // This ensures any computed properties or display logic is updated
        if (this.priceSchedule && this.priceSchedule.id) {
            this.fetchPriceSchedule(this.priceSchedule);
        }
    }

}
</script>
